import {useQuery} from '@tanstack/react-query';
import type {Geometry} from 'geojson';
import {DateTime} from 'luxon';
import urls from './urls.ts';
import {request} from './index.js';
import {INTERPOLATOR, MOSAICKING_ORDER} from '@src/constants/analyze.ts';

export type ProcessRequestParameters = {
  evalscript: string;
  collectionId: string;
  geometry: Geometry;
  width?: number;
  height?: number;
  from?: string;
  to?: string;
  identifier?: 'default' | 'rgb' | 'confidence';
  mosaickingOrder?: MOSAICKING_ORDER;
  downsampling?: INTERPOLATOR;
  upsampling?: INTERPOLATOR;
};

export type ProcessRequestBody = {
  evalscript: string;
  input: {
    bounds: {
      properties: {
        crs:
          | 'http://www.opengis.net/def/crs/EPSG/0/4326'
          | 'http://www.opengis.net/def/crs/EPSG/0/3857';
      };
    } & ({bbox: [number, number, number, number]} | {geometry: Geometry});
    data: {
      type: string;
      id?: string;
      dataFilter: {
        mosaickingOrder?: MOSAICKING_ORDER;
        timeRange: {
          from: string;
          to: string;
        };
      };
      processing?: {
        downsampling?: INTERPOLATOR;
        upsampling?: INTERPOLATOR;
      };
    }[];
  };
  output: {
    responses: {
      identifier: string;
      format: {
        type: string;
      };
    }[];
  } & ({width: number; height: number} | {resx: number; resy: number});
};

const createProcessRequestBody = ({
  evalscript,
  collectionId,
  geometry,
  width = 100,
  height = 100,
  from = DateTime.now().startOf('day').toISO(),
  to = DateTime.now().endOf('day').toISO(),
  mosaickingOrder = MOSAICKING_ORDER.mostRecent,
  downsampling = INTERPOLATOR.bilinear,
  upsampling = INTERPOLATOR.bilinear,
  identifier = 'default',
}: ProcessRequestParameters): ProcessRequestBody => {
  const interpolatedBody: ProcessRequestBody = {
    evalscript,
    input: {
      bounds: {
        properties: {crs: 'http://www.opengis.net/def/crs/EPSG/0/4326'},
        geometry,
      },
      data: [
        {
          dataFilter: {
            mosaickingOrder,
            timeRange: {from, to},
          },
          type: collectionId,
          ...(downsampling || upsampling
            ? {
                processing: {
                  downsampling,
                  upsampling,
                },
              }
            : {}),
        },
      ],
    },
    output: {
      width,
      height,
      responses: [
        {
          identifier,
          format: {
            type: 'image/png',
          },
        },
      ],
    },
  };
  return interpolatedBody;
};

const getProcessImage = async (
  body: ProcessRequestBody,
  version: 'v1' | 'v2' = 'v1',
): Promise<Blob> => {
  const endpoint = version === 'v2' ? urls.processV2 : urls.process;
  const response = await request.post(endpoint, body, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'image/png',
    },
    responseType: 'blob',
  });
  return await response.data;
};

/**
 * Generate a query key for the process image query.
 * This ensures consistent query key generation across components.
 */
export const getProcessImageQueryKey = (
  parameters: Partial<ProcessRequestParameters>,
) => {
  return Array.from(Object.entries(parameters));
};

export const useGetProcessImage = (
  parameters: Partial<ProcessRequestParameters>,
  options?: {
    enabled?: boolean;
    version?: 'v1' | 'v2';
  },
) => {
  const {evalscript, collectionId, geometry} = parameters;
  const {enabled = true, version = 'v1'} = options || {};
  return useQuery({
    queryKey: getProcessImageQueryKey(parameters),
    queryFn: () => {
      const requestBody = createProcessRequestBody(
        parameters as ProcessRequestParameters,
      );
      return getProcessImage(requestBody, version);
    },
    enabled:
      enabled &&
      Boolean(evalscript) &&
      Boolean(collectionId) &&
      Boolean(geometry),
    staleTime: Infinity,
  });
};
